import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  CheckCircle2,
  Circle,
  AlertTriangle,
  Camera,
  FileCheck2,
  ChevronLeft,
  ChevronRight,
  Loader2,
} from "lucide-react";
import Header from "../components/Header";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import SignaturePad from "../components/SignaturePad";
import MediaCapture from "../components/MediaCapture";
import { toast } from "sonner";
import { useApp } from "../context/AppContext";
import { getFormType } from "@/api/formTypes";
import { uploadAsset, createEvidence } from "@/api/evidences";

function dataUrlToBlob(dataUrl) {
  const [meta, b64] = dataUrl.split(",");
  const mime = (meta.match(/:(.*?);/) || [])[1] || "image/png";
  const binary = atob(b64);
  const len = binary.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: mime });
}

export default function Checklist() {
  const { partId } = useParams();
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const { user, formLayout } = useApp();

  const [part, setPart] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const serial = search.get("serial") || "";

  const [values, setValues] = useState({});
  const [media, setMedia] = useState([]);
  const [signature, setSignature] = useState(null);
  const [slideIndex, setSlideIndex] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await getFormType(partId);
        if (!cancelled) setPart(data);
      } catch (err) {
        console.error("formType load", err);
        if (!cancelled) toast.error("No se pudo cargar el tipo de inspección");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [partId]);

  const slides = useMemo(() => {
    if (!part) return [];
    const fieldSlides = (part.sections || []).flatMap((s) =>
      (s.fields || []).map((f) => ({ kind: "field", section: s, field: f }))
    );
    return [...fieldSlides, { kind: "media" }, { kind: "signature" }];
  }, [part]);

  const totals = useMemo(() => {
    if (!part) return { total: 0, completed: 0 };
    const all = (part.sections || []).flatMap((s) => s.fields || []);
    const required = all.filter((f) => f.required);
    const completed = required.filter((f) => {
      const v = values[f.id];
      if (f.type === "boolean") return v === true || v === false;
      return v !== undefined && v !== null && String(v).trim() !== "";
    });
    return { total: required.length, completed: completed.length };
  }, [part, values]);

  if (loading) {
    return (
      <div className="flex-1 flex flex-col bg-white dark:bg-zinc-950">
        <Header title="Cargando..." />
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="w-6 h-6 text-orange-500 animate-spin" />
        </div>
      </div>
    );
  }

  if (!part) {
    return (
      <div className="flex-1 flex flex-col bg-white dark:bg-zinc-950">
        <Header title="No encontrado" />
        <div className="p-6 text-zinc-600 dark:text-zinc-400 font-mono-tech">
          Pieza no encontrada.
        </div>
      </div>
    );
  }

  const setField = (id, val) => setValues((v) => ({ ...v, [id]: val }));

  const progress = totals.total
    ? Math.round((totals.completed / totals.total) * 100)
    : 0;

  const submit = async () => {
    if (totals.completed < totals.total) {
      toast.error("Completa todos los campos requeridos", {
        description: `Faltan ${totals.total - totals.completed} campos`,
      });
      return;
    }
    if (!signature) {
      toast.error("Firma requerida", {
        description: "El ingeniero debe firmar antes de finalizar",
      });
      return;
    }

    setSubmitting(true);
    try {
      let signatureUrl = null;
      try {
        const blob = dataUrlToBlob(signature);
        const file = new File([blob], `firma-${Date.now()}.png`, { type: "image/png" });
        const result = await uploadAsset(file);
        signatureUrl = result?.url || null;
      } catch (err) {
        console.error("signature upload", err);
        toast.error("No se pudo subir la firma");
        setSubmitting(false);
        return;
      }

      const payload = {
        formTypeUid: part.uid,
        serialNumber: serial,
        values,
        media: media.map((m) => ({
          url: m.url,
          type: m.type,
          name: m.name || null,
          mimeType: m.mimeType || null,
          size: m.size || null,
        })),
        signatureUrl,
      };

      const evidence = await createEvidence(payload);
      toast.success("Inspección registrada", {
        description: `${part.name} · SN ${serial}`,
      });
      navigate(`/evidence/${evidence.uid}`, { replace: true });
    } catch (err) {
      console.error("submit", err);
      const message = err?.response?.data?.message || "Error al guardar la inspección";
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const renderField = (f, opts = {}) => {
    const v = values[f.id];
    const big = !!opts.big;
    const inputBase = `bg-zinc-50 dark:bg-zinc-900 border-zinc-200 dark:border-zinc-800 text-zinc-900 dark:text-white font-mono-tech rounded-sm focus-visible:ring-1 focus-visible:ring-orange-500 focus-visible:border-orange-500 placeholder:text-zinc-400 dark:placeholder:text-zinc-700`;
    const h = big ? "h-14" : "h-11";

    if (f.type === "boolean") {
      return (
        <div className={`flex gap-2 ${big ? "" : ""}`}>
          <button
            type="button"
            onClick={() => setField(f.id, true)}
            data-testid={`field-${f.id}-yes`}
            className={`flex-1 ${big ? "h-16 text-sm" : "h-11 text-xs"} rounded-sm border font-mono-tech uppercase tracking-widest transition-colors flex items-center justify-center gap-1.5 ${
              v === true
                ? "bg-green-500/15 border-green-500 text-green-700 dark:text-green-400"
                : "bg-zinc-50 dark:bg-zinc-900 border-zinc-200 dark:border-zinc-800 text-zinc-600 dark:text-zinc-400 hover:border-zinc-300 dark:hover:border-zinc-700"
            }`}
          >
            <CheckCircle2 className={big ? "w-5 h-5" : "w-4 h-4"} />
            Sí · OK
          </button>
          <button
            type="button"
            onClick={() => setField(f.id, false)}
            data-testid={`field-${f.id}-no`}
            className={`flex-1 ${big ? "h-16 text-sm" : "h-11 text-xs"} rounded-sm border font-mono-tech uppercase tracking-widest transition-colors flex items-center justify-center gap-1.5 ${
              v === false
                ? "bg-red-500/15 border-red-500 text-red-700 dark:text-red-400"
                : "bg-zinc-50 dark:bg-zinc-900 border-zinc-200 dark:border-zinc-800 text-zinc-600 dark:text-zinc-400 hover:border-zinc-300 dark:hover:border-zinc-700"
            }`}
          >
            <AlertTriangle className={big ? "w-5 h-5" : "w-4 h-4"} />
            No · KO
          </button>
        </div>
      );
    }
    if (f.type === "number") {
      return (
        <div className="relative">
          <Input
            type="number"
            inputMode="decimal"
            min={f.min}
            max={f.max}
            step="0.01"
            value={v ?? ""}
            onChange={(e) => setField(f.id, e.target.value)}
            data-testid={`field-${f.id}`}
            placeholder="0.00"
            className={`${inputBase} ${h} pr-14`}
          />
          {f.unit && (
            <span className="absolute right-3 top-1/2 -translate-y-1/2 font-mono-tech text-[10px] uppercase tracking-widest text-orange-500">
              {f.unit}
            </span>
          )}
        </div>
      );
    }
    if (f.type === "select") {
      return (
        <Select value={v ?? ""} onValueChange={(val) => setField(f.id, val)}>
          <SelectTrigger
            data-testid={`field-${f.id}`}
            className={`${inputBase} ${h}`}
          >
            <SelectValue placeholder="Selecciona…" />
          </SelectTrigger>
          <SelectContent className="bg-white dark:bg-zinc-900 border-zinc-200 dark:border-zinc-800 text-zinc-900 dark:text-white font-mono-tech">
            {(f.options || []).map((opt) => (
              <SelectItem key={opt} value={opt} className="font-mono-tech text-xs">
                {opt}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    }
    return (
      <Textarea
        value={v ?? ""}
        onChange={(e) => setField(f.id, e.target.value)}
        data-testid={`field-${f.id}`}
        rows={big ? 4 : 3}
        placeholder="Escribe observaciones técnicas…"
        className={`${inputBase} text-sm`}
      />
    );
  };

  const isFieldFilled = (f) => {
    const v = values[f.id];
    if (f.type === "boolean") return v === true || v === false;
    return v !== undefined && v !== null && String(v).trim() !== "";
  };

  if (formLayout === "slides") {
    const current = slides[slideIndex];
    const isLast = slideIndex === slides.length - 1;
    const isFirst = slideIndex === 0;

    const goNext = () => setSlideIndex((i) => Math.min(i + 1, slides.length - 1));
    const goPrev = () => setSlideIndex((i) => Math.max(i - 1, 0));

    return (
      <div className="flex-1 flex flex-col overflow-hidden bg-white dark:bg-zinc-950" data-testid="checklist-page">
        <Header title={part.name} subtitle={`SN ${serial || "—"}`} />

        <div className="px-5 pt-3 pb-3 border-b border-zinc-200 dark:border-zinc-800 shrink-0">
          <div className="flex items-center justify-between mb-2">
            <span className="font-mono-tech text-[10px] uppercase tracking-widest text-zinc-500">
              Paso {slideIndex + 1} / {slides.length}
            </span>
            <span className="font-mono-tech text-[10px] uppercase tracking-widest text-orange-500">
              {totals.completed}/{totals.total} req. · {progress}%
            </span>
          </div>
          <div className="flex gap-0.5">
            {slides.map((_, i) => (
              <div
                key={i}
                className={`h-1 flex-1 rounded-full transition-colors ${
                  i < slideIndex
                    ? "bg-orange-500"
                    : i === slideIndex
                      ? "bg-orange-500"
                      : "bg-zinc-200 dark:bg-zinc-800"
                }`}
              />
            ))}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto no-scrollbar px-6 py-6 flex flex-col" data-testid={`slide-${slideIndex}`}>
          {current?.kind === "field" && (
            <div className="flex-1 flex flex-col">
              <div className="flex items-center gap-2 mb-3">
                <Circle className="w-2 h-2 fill-orange-500 text-orange-500" />
                <span className="font-mono-tech text-[10px] uppercase tracking-widest text-orange-500">
                  {current.section.title}
                </span>
              </div>
              <h2 className="font-display font-bold text-2xl uppercase leading-tight tracking-tight text-zinc-900 dark:text-white mb-1">
                {current.field.label}
                {current.field.required && (
                  <span className="text-orange-500 ml-2">*</span>
                )}
              </h2>
              <p className="font-mono-tech text-[10px] uppercase tracking-widest text-zinc-500 mb-6">
                {current.field.required ? "Campo obligatorio" : "Opcional"}
                {current.field.unit ? ` · Unidad: ${current.field.unit}` : ""}
              </p>
              <div className="mb-4">{renderField(current.field, { big: true })}</div>
              {current.field.required && !isFieldFilled(current.field) && (
                <div className="mt-auto p-3 rounded-sm bg-orange-500/10 border border-orange-500/30 flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-orange-500 mt-0.5 shrink-0" />
                  <p className="font-mono-tech text-[11px] text-orange-700 dark:text-orange-400">
                    Necesario para completar la inspección. Puedes responder y continuar.
                  </p>
                </div>
              )}
            </div>
          )}

          {current?.kind === "media" && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Camera className="w-3.5 h-3.5 text-orange-500" />
                <span className="font-mono-tech text-[10px] uppercase tracking-widest text-orange-500">
                  Evidencias
                </span>
              </div>
              <h2 className="font-display font-bold text-2xl uppercase leading-tight tracking-tight text-zinc-900 dark:text-white mb-1">
                Fotos y vídeo
              </h2>
              <p className="font-mono-tech text-[10px] uppercase tracking-widest text-zinc-500 mb-6">
                Adjunta evidencias visuales · Opcional
              </p>
              <MediaCapture media={media} onChange={setMedia} />
            </div>
          )}

          {current?.kind === "signature" && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <FileCheck2 className="w-3.5 h-3.5 text-orange-500" />
                <span className="font-mono-tech text-[10px] uppercase tracking-widest text-orange-500">
                  Validación final
                </span>
              </div>
              <h2 className="font-display font-bold text-2xl uppercase leading-tight tracking-tight text-zinc-900 dark:text-white mb-1">
                Firma del ingeniero
                <span className="text-orange-500 ml-2">*</span>
              </h2>
              <p className="font-mono-tech text-[10px] uppercase tracking-widest text-zinc-500 mb-6">
                Firma para emitir la evidencia de garantía
              </p>
              <SignaturePad value={signature} onChange={setSignature} />
            </div>
          )}
        </div>

        <div className="border-t border-zinc-200 dark:border-zinc-800 bg-white/95 dark:bg-zinc-950/95 backdrop-blur-md p-4 shrink-0 flex gap-2">
          <button
            onClick={goPrev}
            disabled={isFirst}
            data-testid="slide-prev-btn"
            className="h-12 w-12 shrink-0 rounded-sm bg-zinc-100 dark:bg-zinc-800 hover:bg-zinc-200 dark:hover:bg-zinc-700 disabled:opacity-40 disabled:cursor-not-allowed text-zinc-900 dark:text-white border border-zinc-200 dark:border-zinc-700 transition-colors flex items-center justify-center"
            aria-label="Anterior"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          {isLast ? (
            <button
              onClick={submit}
              disabled={submitting}
              data-testid="checklist-submit-btn"
              className="flex-1 bg-orange-500 hover:bg-orange-600 disabled:bg-zinc-400 text-white font-display font-bold uppercase tracking-widest h-12 rounded-sm transition-all glow-orange flex items-center justify-center gap-2"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Generar evidencia"}
            </button>
          ) : (
            <button
              onClick={goNext}
              data-testid="slide-next-btn"
              className="flex-1 group bg-orange-500 hover:bg-orange-600 text-white font-display font-bold uppercase tracking-widest h-12 rounded-sm transition-all glow-orange flex items-center justify-center gap-2"
            >
              Siguiente
              <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-white dark:bg-zinc-950" data-testid="checklist-page">
      <Header title={part.name} subtitle={`SN ${serial || "—"}`} />

      <div className="px-5 pt-3 pb-2 border-b border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950 shrink-0">
        <div className="flex items-center justify-between mb-1.5">
          <span className="font-mono-tech text-[10px] uppercase tracking-widest text-zinc-500">
            Progreso
          </span>
          <span className="font-mono-tech text-[10px] uppercase tracking-widest text-orange-500">
            {totals.completed}/{totals.total} · {progress}%
          </span>
        </div>
        <div className="h-1 bg-zinc-100 dark:bg-zinc-900 rounded-full overflow-hidden">
          <div
            className="h-full bg-orange-500 transition-all"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto no-scrollbar px-5 pt-5 pb-24">
        {(part.sections || []).map((section, sIdx) => (
          <section key={section.title + sIdx} className="mb-7" data-testid={`section-${sIdx}`}>
            <div className="flex items-center gap-2 mb-3">
              <Circle className="w-2 h-2 fill-orange-500 text-orange-500" />
              <h3 className="font-display font-bold text-sm uppercase tracking-widest text-zinc-900 dark:text-white">
                {section.title}
              </h3>
              <div className="flex-1 h-px bg-zinc-200 dark:bg-zinc-800" />
              <span className="font-mono-tech text-[9px] uppercase tracking-widest text-zinc-400 dark:text-zinc-600">
                {String(sIdx + 1).padStart(2, "0")}
              </span>
            </div>
            <div className="space-y-4">
              {(section.fields || []).map((f) => (
                <div key={f.id}>
                  <label className="font-mono-tech text-xs text-zinc-700 dark:text-zinc-300 mb-1.5 flex items-start gap-1">
                    <span className="flex-1">{f.label}</span>
                    {f.required && (
                      <span className="text-orange-500 font-bold leading-none">*</span>
                    )}
                  </label>
                  {renderField(f)}
                </div>
              ))}
            </div>
          </section>
        ))}

        <section className="mb-7">
          <div className="flex items-center gap-2 mb-3">
            <Camera className="w-3.5 h-3.5 text-orange-500" />
            <h3 className="font-display font-bold text-sm uppercase tracking-widest text-zinc-900 dark:text-white">
              Evidencias multimedia
            </h3>
            <div className="flex-1 h-px bg-zinc-200 dark:bg-zinc-800" />
            <span className="font-mono-tech text-[9px] uppercase tracking-widest text-zinc-400 dark:text-zinc-600">
              {media.length} arch.
            </span>
          </div>
          <MediaCapture media={media} onChange={setMedia} />
        </section>

        <section className="mb-4">
          <div className="flex items-center gap-2 mb-3">
            <FileCheck2 className="w-3.5 h-3.5 text-orange-500" />
            <h3 className="font-display font-bold text-sm uppercase tracking-widest text-zinc-900 dark:text-white">
              Firma y validación
            </h3>
            <div className="flex-1 h-px bg-zinc-200 dark:bg-zinc-800" />
          </div>
          <SignaturePad value={signature} onChange={setSignature} />
        </section>
      </div>

      <div className="border-t border-zinc-200 dark:border-zinc-800 bg-white/95 dark:bg-zinc-950/95 backdrop-blur-md p-4 shrink-0">
        <button
          onClick={submit}
          disabled={submitting}
          data-testid="checklist-submit-btn"
          className="w-full bg-orange-500 hover:bg-orange-600 disabled:bg-zinc-400 text-white font-display font-bold uppercase tracking-widest h-13 py-4 rounded-sm transition-all glow-orange active:scale-[0.98] flex items-center justify-center gap-2"
        >
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Generar evidencia de garantía"}
        </button>
      </div>
    </div>
  );
}
